/**
 * Achievements View Component
 * 
 * Gallery view of all user achievements
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  RefreshControl,
  ActivityIndicator,
  Pressable,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Screen } from '@/components/layout/Screen';
import { Heading, Body, Caption } from '@/components/ui/Text';
import { AchievementCard } from './AchievementCard';
import { ErrorState } from '@/components/ui/ErrorState';
import { useTheme } from '@/theme';
import { useUserStore } from '@/state/userStore';
import {
  getUserAchievements,
  checkAndUpdateAchievements,
  calculateAchievementProgress,
  ACHIEVEMENT_DEFINITIONS,
} from '@/services/achievements';
import { getUserStats, updateUserStatsFromTrips } from '@/services/userStats';
import { fetchUserTrips } from '@/services/trips';
import {
  getCountriesFromTrips,
  getCitiesFromTrips,
  calculateTotalTripDays,
  getContinentsFromCountries,
  calculateTravelStreak,
  getTripsEligibleForTravelStats,
} from '@/services/gamification';
import type { Achievement, AchievementProgress } from '@/services/achievements';
import type { UserStats } from '@/services/userStats';

type AchievementCategory = 'all' | 'explorer' | 'landmark' | 'style' | 'milestone';

function buildDerivedStatsFromTrips(userId: string, trips: Awaited<ReturnType<typeof fetchUserTrips>>['trips']): UserStats {
  const eligibleTrips = getTripsEligibleForTravelStats(trips);
  const countries = getCountriesFromTrips(eligibleTrips);
  const cities = getCitiesFromTrips(eligibleTrips);
  const totalTripDays = calculateTotalTripDays(eligibleTrips);
  const continents = getContinentsFromCountries(countries);
  const { currentStreak, longestStreak } = calculateTravelStreak(eligibleTrips);

  return {
    userId,
    countriesVisited: countries,
    citiesVisited: cities,
    landmarksVisited: [],
    totalTripDays,
    continentsVisited: continents,
    currentStreak,
    longestStreak,
    lastUpdated: new Date(),
  };
}

export function AchievementsView() {
  const theme = useTheme();
  const router = useRouter();
  const handleClose = () => {
    if (typeof router.canGoBack === 'function' && router.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/(tabs)/profile');
  };
  const closeButton = (
    <Pressable
      onPress={handleClose}
      style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
      accessibilityLabel="Close achievements"
      accessibilityRole="button"
    >
      <Ionicons name="close" size={24} color={theme.colors.text} />
    </Pressable>
  );

  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<AchievementCategory>('all');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [achievementsError, setAchievementsError] = useState<string | null>(null);

  useEffect(() => {
    loadAchievements();
  }, [firebaseUser?.uid]);

  const loadAchievements = async () => {
    if (!firebaseUser?.uid) {
      setAchievements([]);
      setLoading(false);
      setRefreshing(false);
      return;
    }

    try {
      setAchievementsError(null);
      setLoading(true);
      let stats = null;
      let trips: Awaited<ReturnType<typeof fetchUserTrips>>['trips'] = [];
      let syncError = false;

      // Fetch stats and trips to calculate fresh progress.
      try {
        stats = await getUserStats(firebaseUser.uid);
        const tripsResult = await fetchUserTrips(firebaseUser.uid);
        trips = tripsResult.trips;

        // Backfill userStats from trips if doc is missing but user has trips.
        if (!stats && trips.length > 0) {
          try {
            stats = await updateUserStatsFromTrips(firebaseUser.uid, trips);
          } catch {
            // Keep showing whatever progress we can.
          }
        }
      } catch (syncLoadError) {
        syncError = true;
        console.warn('[AchievementsView] Unable to sync latest data:', syncLoadError);
      }

      if (stats) {
        try {
          await checkAndUpdateAchievements(firebaseUser.uid, stats, trips);
        } catch (syncWriteError) {
          syncError = true;
          console.warn('[AchievementsView] Unable to write updated achievements:', syncWriteError);
        }
      }

      // Fetch saved achievements (returns [] if unavailable).
      const savedProgressList = await getUserAchievements(firebaseUser.uid);
      let progressList = savedProgressList;
      if (progressList.length === 0 && trips.length > 0) {
        const derivedStats = stats ?? buildDerivedStatsFromTrips(firebaseUser.uid, trips);
        progressList = calculateAchievementProgress(derivedStats, trips);
      }
      
      // Convert to Achievement objects
      const achievementList: Achievement[] = Object.keys(ACHIEVEMENT_DEFINITIONS).map((id) => {
        const definition = ACHIEVEMENT_DEFINITIONS[id as keyof typeof ACHIEVEMENT_DEFINITIONS];
        const progress = progressList.find(p => p.achievementId === id);
        
        return {
          ...definition,
          progress: progress?.progress || 0,
          unlocked: progress?.unlocked || false,
          unlockedAt: progress?.unlockedAt,
        };
      });

      setAchievements(achievementList);
      if (syncError) {
        setAchievementsError("Couldn't sync latest achievements. Showing local progress.");
      }
    } catch (error) {
      console.error('[AchievementsView] Error loading achievements:', error);
      setAchievementsError("Couldn't load achievements. Check your connection and try again.");
      // Keep base achievements visible even if all reads fail.
      const fallbackAchievements: Achievement[] = Object.values(ACHIEVEMENT_DEFINITIONS).map((definition) => ({
        ...definition,
        progress: 0,
        unlocked: false,
      }));
      setAchievements(fallbackAchievements);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadAchievements();
  };

  const categories: Array<{ id: AchievementCategory; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'explorer', label: 'Explorer' },
    { id: 'landmark', label: 'Landmark' },
    { id: 'style', label: 'Style' },
    { id: 'milestone', label: 'Milestone' },
  ];

  const filteredAchievements = achievements.filter((achievement) => {
    if (selectedCategory === 'all') return true;
    return achievement.category === selectedCategory;
  });

  const unlockedCount = achievements.filter(a => a.unlocked).length;
  const totalCount = achievements.length;

  // Get recently unlocked achievements (last 5, sorted by unlock date)
  const recentlyUnlocked = achievements
    .filter(a => a.unlocked && a.unlockedAt)
    .sort((a, b) => {
      if (!a.unlockedAt || !b.unlockedAt) return 0;
      return new Date(b.unlockedAt).getTime() - new Date(a.unlockedAt).getTime();
    })
    .slice(0, 5);

  if (loading && achievements.length === 0) {
    return (
      <Screen
        title="Achievements"
        headerAction={closeButton}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.accent} />
        </View>
      </Screen>
    );
  }

  if (achievementsError && achievements.length === 0) {
    return (
      <Screen
        title="Achievements"
        headerAction={closeButton}
      >
        <ErrorState
          variant="full"
          title="Couldn't load achievements"
          message={achievementsError}
          onRetry={loadAchievements}
          retryLabel="Retry"
        />
      </Screen>
    );
  }

  return (
    <Screen
      title="Achievements"
      subtitle={`${unlockedCount} of ${totalCount} unlocked`}
      headerAction={closeButton}
    >
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {achievementsError ? (
          <View style={{ marginBottom: 16 }}>
            <ErrorState
              title="Couldn't refresh achievements"
              message={achievementsError}
              onRetry={loadAchievements}
              retryLabel="Retry"
            />
          </View>
        ) : null}


        {/* Recently Unlocked Section */}
        {recentlyUnlocked.length > 0 && selectedCategory === 'all' && (
          <View style={styles.recentlyUnlockedSection}>
            <View style={styles.sectionHeader}>
              <Heading style={styles.sectionTitle}>Recently Unlocked</Heading>
              <Body style={[styles.sectionSubtitle, { color: theme.colors.textSecondary }]}>
                Your latest achievements
              </Body>
            </View>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.recentlyUnlockedList}
            >
              {recentlyUnlocked.map((achievement) => (
                <View key={achievement.id} style={styles.recentAchievementCard}>
                  <AchievementCard
                    achievement={achievement}
                    showProgress={false}
                  />
                </View>
              ))}
            </ScrollView>
          </View>
        )}

        {/* Category Filter */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.categoryFilter}
          contentContainerStyle={styles.categoryFilterContent}
        >
          {categories.map((category) => {
            const isSelected = selectedCategory === category.id;
            return (
              <Pressable
                key={category.id}
                style={({ pressed }) => [
                  styles.categoryButton,
                  {
                    backgroundColor: isSelected
                      ? theme.colors.accent
                      : theme.colors.surface,
                    borderWidth: isSelected ? 0 : 1,
                    borderColor: isSelected ? 'transparent' : theme.colors.border,
                  },
                  pressed && { opacity: 0.8 },
                ]}
                onPress={() => setSelectedCategory(category.id)}
              >
                <Body
                  style={[
                    styles.categoryButtonText,
                    {
                      color: isSelected
                        ? theme.colors.onAccent
                        : theme.colors.text,
                    },
                  ]}
                >
                  {category.label}
                </Body>
              </Pressable>
            );
          })}
        </ScrollView>

        {/* Achievements Grid */}
        <View style={styles.achievementsGrid}>
          {filteredAchievements.map((achievement) => (
            <AchievementCard
              key={achievement.id}
              achievement={achievement}
              showProgress={!achievement.unlocked}
            />
          ))}
        </View>

        {filteredAchievements.length === 0 && (
          <View style={styles.emptyState}>
            <Body style={{ color: theme.colors.textSecondary, textAlign: 'center' }}>
              No achievements in this category yet.
            </Body>
          </View>
        )}
      </ScrollView>
    </Screen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  categoryFilter: {
    marginBottom: 24,
  },
  categoryFilterContent: {
    gap: 8,
    paddingRight: 16,
  },
  categoryButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  categoryButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  achievementsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'flex-start',
  },
  emptyState: {
    padding: 24,
    alignItems: 'center',
  },
  recentlyUnlockedSection: {
    marginBottom: 24,
  },
  sectionHeader: {
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 14,
  },
  recentlyUnlockedList: {
    gap: 12,
    paddingRight: 16,
  },
  recentAchievementCard: {
    width: 180,
  },
});

